import { isReachable } from '../../shared/lib/businessNumber.js';
import type { Availability, WeeklySchedule } from '@acs/shared';
import type { BusinessDoc, ServiceDoc, StaffDoc } from '../../shared/api/converters.js';
import type { TranslationKey } from '../../i18n/index.js';

/**
 * "Is this business actually finished?" — the whole configuration, not just the
 * wizard.
 *
 * `onboardingSteps` answers a narrower question: which of the five wizard steps
 * is still outstanding. It deliberately stops there, because the wizard has to
 * end somewhere an owner can reach in one sitting. This module answers the one
 * the header asks instead — *everything* that has to be true before we tell an
 * owner their receptionist is set up, including the parts the wizard never
 * covers (staff). The two overlap on purpose and must agree where they do, so
 * the shared rules (`isReachable`, "one active service", "some opening hours")
 * are read from the same helpers rather than restated here.
 *
 * Everything is derived from live configuration — nothing is stamped. A green
 * check that survived the owner deleting their last service would be worse than
 * no indicator at all.
 */

export type SetupTaskId = 'forwarding' | 'services' | 'hours' | 'assistant' | 'staff';

export interface SetupTask {
  id: SetupTaskId;
  /** Imperative label for the banner's button — names the missing thing.
   *  A catalogue key, not a sentence: this module stays pure and testable
   *  without a translator in scope (doc 20 D8). */
  ctaKey: TranslationKey;
  /** One sentence: what is missing, and what it costs while it is. */
  detailKey: TranslationKey;
  /** Where the fix happens, relative to `/b/{businessId}`. */
  href: string;
  /**
   * Reserved for the one failure the owner cannot see: nobody reaches them at
   * all. Everything else leaves a phone that rings and an assistant that
   * answers, however badly, and is reported as a warning.
   *
   * The distinction exists because this banner is permanent until fixed. An
   * indicator that shows red for a missing staff member — while bookings are in
   * fact being taken against business-wide capacity (doc 09 D3) — spends the
   * one signal that should make an owner stop what they are doing.
   */
  blocking: boolean;
}

interface Check extends SetupTask {
  satisfied: (input: ResolvedInput) => boolean;
}

/**
 * The checklist, in priority order — first unsatisfied entry is what the banner
 * sends the owner to. Ordered by what is actually broken while it is missing,
 * NOT by the wizard's teaching order:
 *
 *  1. forwarding — no call arrives at all; nothing below it can matter.
 *  2. services   — the AI has nothing to offer, so every call dead-ends.
 *  3. hours      — nothing to offer it *in*: no slot can ever be quoted.
 *  4. assistant  — it answers, with no words of its own.
 *  5. staff      — only when there IS active staff and none of it can take a
 *                  booking. Staff is optional (doc 56): with nobody marked
 *                  active the business books on its own hours (doc 09 D3),
 *                  which is a complete setup, not a gap. Once anyone is active
 *                  the assistant books only them, so active-but-unbookable
 *                  staff leaves calls with nothing to offer.
 */
const CHECKS: Check[] = [
  {
    id: 'forwarding',
    ctaKey: 'setup.forwarding.cta',
    detailKey: 'setup.forwarding.detail',
    href: '/settings?section=number',
    blocking: true,
    satisfied: ({ business }) => isReachable(business),
  },
  {
    id: 'services',
    ctaKey: 'setup.services.cta',
    detailKey: 'setup.services.detail',
    href: '/settings?section=services',
    blocking: false,
    satisfied: ({ activeServices }) => activeServices.length > 0,
  },
  {
    id: 'hours',
    ctaKey: 'setup.hours.cta',
    detailKey: 'setup.hours.detail',
    href: '/settings?section=hours',
    blocking: false,
    satisfied: ({ availability }) => hasWorkingDay(availability?.hours),
  },
  {
    id: 'assistant',
    ctaKey: 'setup.assistant.cta',
    detailKey: 'setup.assistant.detail',
    href: '/settings?section=assistant',
    blocking: false,
    // Greeting only: `language` is a four-value enum with a country-derived
    // default, so there is no state in which it is unset. The wizard gates its
    // assistant step on exactly this, and the two must not disagree.
    satisfied: ({ business }) => business.greeting.trim().length > 0,
  },
  {
    id: 'staff',
    ctaKey: 'setup.staff.cta',
    detailKey: 'setup.staff.detail',
    href: '/staff',
    blocking: false,
    satisfied: (input) =>
      !input.staff.some((member) => member.active) || input.staff.some((member) => isBookable(member, input)),
  },
];

export interface SetupInput {
  business: BusinessDoc;
  services: ServiceDoc[] | undefined;
  availability: Availability | null | undefined;
  staff: StaffDoc[] | undefined;
}

interface ResolvedInput {
  business: BusinessDoc;
  activeServices: ServiceDoc[];
  availability: Availability | null;
  staff: StaffDoc[];
}

export interface SetupStatus {
  /** Outstanding tasks, highest priority first. Empty once fully configured. */
  missing: SetupTask[];
  /** Where to send the owner now — `null` when nothing is outstanding. */
  next: SetupTask | null;
  /** Every check satisfied. Only ever true once {@link SetupStatus.loaded}. */
  complete: boolean;
  /**
   * All three subscriptions have delivered. Until then the answer is *unknown*,
   * not *incomplete* — a header that flashes a warning on every page load for
   * businesses that are perfectly configured trains owners to ignore it.
   */
  loaded: boolean;
}

export function businessSetupStatus({ business, services, availability, staff }: SetupInput): SetupStatus {
  const loaded = services !== undefined && availability !== undefined && staff !== undefined;
  if (!loaded) return { missing: [], next: null, complete: false, loaded: false };

  const resolved: ResolvedInput = {
    business,
    activeServices: (services ?? []).filter((s) => s.active),
    availability: availability ?? null,
    staff: staff ?? [],
  };

  const missing = CHECKS.filter((c) => !c.satisfied(resolved)).map(toTask);
  return { missing, next: missing[0] ?? null, complete: missing.length === 0, loaded: true };
}

/** Drop the predicate — callers get the description, never the rule. */
function toTask({ satisfied: _satisfied, ...task }: Check): SetupTask {
  return task;
}

/** Does a weekly `hours` map open on any day at all? */
function hasWorkingDay(hours: WeeklySchedule['hours'] | undefined): boolean {
  return Object.values(hours ?? {}).some((intervals) => (intervals?.length ?? 0) > 0);
}

/**
 * Can this person actually take a booking? — services AND hours, which is what
 * "a staff member with selected services and working hours" means once the
 * defaults are taken seriously.
 *
 * Both fields encode "unset" as a *deliberate* inheriting default rather than a
 * gap, and treating either as incomplete would flag correctly-configured
 * businesses:
 *
 *  - `serviceIds: []` means "performs ALL services" (doc 09 D6) — the default,
 *    and the right answer for the single-person salon this product is aimed at.
 *  - `schedule: null` means "works the business's hours" (doc 09 D2), which is
 *    how every staff member starts.
 *
 * So the check is on what the person *resolves to*, not on whether the owner
 * typed something: a member restricted to services that have since been deleted
 * or deactivated is genuinely unbookable and does count as missing.
 */
function isBookable(member: StaffDoc, { activeServices, availability }: ResolvedInput): boolean {
  if (!member.active) return false;

  const performsSomething =
    member.serviceIds.length === 0
      ? activeServices.length > 0
      : member.serviceIds.some((id) => activeServices.some((s) => s.id === id));

  const worksSometime = member.schedule
    ? hasWorkingDay(member.schedule.hours)
    : hasWorkingDay(availability?.hours);

  return performsSomething && worksSometime;
}
